import * as React from "react";
import { observer } from "mobx-react";

import Constants from "const/constants";

import Icon from "../../decorations/icons/icon";
import Player from "../../players/player";
import Tooltip from "../../tooltip/tooltip";
import type { DropdownSectionItem } from "../../context-menu/contextmenu";
import { ContextMenuButton } from "../../context-menu/contextmenu";

import Upload from "./upload/upload";
import Input from "./input/input";

import { Wrapper, Footer, FooterText, Inner, Preview } from "./default.styles";

type UploaderType = "image" | "video";

export interface UploaderProps {
    uploaderStore: any; //TODO: needs typing
    name?: string;
    defaultPreviewOptions?: {
        previewURL?: string;
        type?: UploaderType;
    };
    footerText?: string;
    isCompact?: boolean;
    isSquare?: boolean;
    displayErrorMsg?: boolean;
    footerTooltipText?: string;
    width?: string;
    height?: string;
    extraDropdownOptions?: DropdownSectionItem[];
    className?: string;
    CustomMenuButton?: React.ComponentType;
    maxUploadSizeAllowed?: number;
}

const Uploader = (props: UploaderProps) => {
    const {
        uploaderStore,
        name = "",
        defaultPreviewOptions,
        footerText = "",
        isCompact,
        isSquare,
        displayErrorMsg = true,
        footerTooltipText,
        //TODO: fixed values are generally bad
        width = "297px",
        height = "169px",
        extraDropdownOptions = [],
        className,
        CustomMenuButton,
        maxUploadSizeAllowed
    } = props;

    const hiddenFileInput = React.useRef(null);
    const currentUpload = uploaderStore.uploadsArray[0];

    let previewOptions = !currentUpload && defaultPreviewOptions;

    const removeCurrentUpload = () => {
        uploaderStore.removeUpload(currentUpload.id);
    };

    if (currentUpload?.shouldShowPreview) {
        let type;
        if (currentUpload.type === `video`) {
            type = Constants.MEDIA_TYPE.CLIP;
        } else {
            type = currentUpload.type;
        }
        previewOptions = {
            type,
            previewURL: currentUpload.previewURL
        };
    }

    const onFiles = (files: File[]) => {
        const file = files[0];

        if (currentUpload) removeCurrentUpload();

        uploaderStore.uploadFile(file, maxUploadSizeAllowed);
    };

    const onCancel = () => {
        currentUpload.stop();
        removeCurrentUpload();
    };

    const dropdownOptions: DropdownSectionItem[] = [
        {
            id: `upload`,
            onClick: () => hiddenFileInput.current.click(),
            text: `File upload`
        },
        {
            id: `cloud upload`,
            onClick: uploaderStore.uploadFromFilestack,
            text: `Cloud service upload`
        },
        ...extraDropdownOptions
    ];

    return (
        <Wrapper
            width={width}
            height={isSquare ? `auto` : height}
            className={className}
        >
            <Inner isCompact={isCompact} isSquare={isSquare}>
                {(() => {
                    if (previewOptions) {
                        return (
                            <Preview>
                                <Player
                                    type={previewOptions.type}
                                    src={previewOptions.previewURL}
                                />
                            </Preview>
                        );
                    } else if (currentUpload) {
                        return (
                            <Upload
                                uploadStore={currentUpload}
                                onCancel={onCancel}
                                isCompact={isCompact}
                                displayErrorMsg={displayErrorMsg}
                            />
                        );
                    } else {
                        return (
                            <p>
                                {!isCompact && (
                                    <>
                                        <Icon name={`upload`} />
                                        <br />
                                    </>
                                )}
                                <b>Drop files here</b>
                                <br />
                                <span>or click sub menu to upload</span>
                            </p>
                        );
                    }
                })()}
                <Input name={name} onFiles={onFiles} ref={hiddenFileInput} />
            </Inner>
            <Footer>
                <FooterText isCompact={isCompact}>
                    {footerText}
                    {isCompact && (
                        <Tooltip position="top" innerHtml={footerTooltipText}>
                            <Icon name="info" />
                        </Tooltip>
                    )}
                </FooterText>
                <ContextMenuButton
                    dropdownOptions={dropdownOptions}
                    CustomMenuButton={CustomMenuButton}
                />
            </Footer>
        </Wrapper>
    );
};

export default observer(Uploader);
